import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { MAX_SUGGESTIONS, dedupeSuggestions } from '@/lib/agentSuggestions'

export type SuggestionPillsProps = {
  suggestions: string[]
  onSelect: (suggestion: string) => void
  hidden?: boolean
  className?: string
}

export function SuggestionPills({ suggestions, onSelect, hidden, className }: SuggestionPillsProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const items = dedupeSuggestions(suggestions).slice(0, MAX_SUGGESTIONS)
  if (hidden || items.length === 0) return null

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const delta = e.key === 'ArrowRight' ? 1 : -1
    const next = (index + delta + items.length) % items.length
    refs.current[next]?.focus()
  }

  return (
    <div
      role="listbox"
      aria-label="Suggestions"
      aria-orientation="horizontal"
      className={cn('flex flex-wrap gap-2', className)}
    >
      {items.map((s, i) => (
        <button
          key={s}
          ref={(el) => {
            refs.current[i] = el
          }}
          type="button"
          role="option"
          aria-selected={false}
          tabIndex={i === 0 ? 0 : -1}
          onClick={() => onSelect(s)}
          onKeyDown={(e) => handleKeyDown(e, i)}
          className="rounded-full border border-violet-200 bg-white px-3 py-1.5 text-sm text-violet-700 transition-colors hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          {s}
        </button>
      ))}
    </div>
  )
}
